/* import __COLOCATED_TEMPLATE__ from './runbook-selector.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type {
  FinVoiceRunbook,
  FinVoiceProcedure,
  WorkflowSelection,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import {
  getRunbookOptions,
  FIN_VOICE_RUNBOOK_FEATURE_FLAGS,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';

interface Signature {
  Element: HTMLElement;
  Args: {
    // New unified API
    selectedWorkflow?: WorkflowSelection;
    procedures?: FinVoiceProcedure[];
    onChange?: (selection: WorkflowSelection) => void;

    // Legacy API (for backward compatibility)
    selectedRunbook?: FinVoiceRunbook;
    onRunbookChange?: (runbook: FinVoiceRunbook) => void;

    // UI configuration
    filterPlaceholder?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
  };
}

export default class RunbookSelector extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  /**
   * Detect which API is being used
   */
  get isUnifiedMode(): boolean {
    return !!(this.args.selectedWorkflow !== undefined || this.args.procedures);
  }

  /**
   * Should procedures group be shown?
   */
  get shouldShowProcedures(): boolean {
    return !!(
      this.args.procedures?.length && this.appService.app.canUseFeature('fin-voice-procedures')
    );
  }

  /**
   * Get all available values as a Set for efficient lookup
   */
  get availableValues(): Set<string> {
    let values = this.groupedOptions.flatMap((group) => group.items.map((item) => item.value));
    return new Set(values);
  }

  /**
   * Get the selected value for the IcSelect component
   * Handles both unified mode and legacy mode
   * Returns undefined if the value doesn't exist in the options to prevent assertion errors
   */
  get selectedValue(): string | undefined {
    let value: string | undefined;

    if (this.args.selectedWorkflow) {
      if (this.args.selectedWorkflow.type === 'procedure') {
        let procedureId = this.args.selectedWorkflow.procedureId;
        value = procedureId ? `procedure:${procedureId}` : undefined;
      } else {
        value = this.args.selectedWorkflow.runbook;
      }
    } else {
      value = this.args.selectedRunbook;
    }

    // Validate that the value exists in the options
    if (value && !this.availableValues.has(value)) {
      return undefined;
    }

    return value;
  }

  /**
   * Get runbook options (legacy behavior)
   */
  get runbookGroupedOptions() {
    let includeToast = this.appService.app.canUseFinVoiceToastRunbooks;

    return getRunbookOptions(includeToast).map((group) => ({
      heading: group.heading ? this.intl.t(group.heading) : undefined,
      items: group.items
        .filter((option) => this.shouldIncludeRunbook(option.value))
        .map((option) => ({
          value: option.value,
          text: this.intl.t(option.text),
        })),
    }));
  }

  /**
   * Get unified options with both runbooks and procedures
   */
  get unifiedGroupedOptions() {
    let groups: Array<{ heading?: string; items: Array<{ value: string; text: string }> }> = [];

    // Runbooks first (always shown)
    groups.push(...this.runbookGroupedOptions);

    // Procedures second (when flag enabled and procedures available)
    if (this.shouldShowProcedures) {
      groups.push({
        heading: this.intl.t('ai-agent.voice-playground.settings.runbook-group-procedures'),
        items: (this.args.procedures || []).map((proc) => ({
          value: `procedure:${proc.id}`,
          text: proc.name,
        })),
      });
    }

    return groups;
  }

  /**
   * Get the appropriate grouped options based on mode
   */
  get groupedOptions() {
    return this.isUnifiedMode ? this.unifiedGroupedOptions : this.runbookGroupedOptions;
  }

  /**
   * Check if a runbook should be included based on feature flags
   * @param runbookValue - The runbook value to check
   * @returns true if the runbook should be included
   */
  shouldIncludeRunbook(runbookValue: FinVoiceRunbook): boolean {
    let featureFlag = FIN_VOICE_RUNBOOK_FEATURE_FLAGS[runbookValue];
    return featureFlag ? this.appService.app.canUseFeature(featureFlag) : true;
  }

  @action
  onRunbookChange(value: string) {
    let selection: WorkflowSelection;

    if (value.startsWith('procedure:')) {
      // Handle procedure selection
      let procedureId = value.substring('procedure:'.length);
      selection = { type: 'procedure', procedureId };
    } else {
      // Handle runbook selection
      selection = { type: 'runbook', runbook: value as FinVoiceRunbook };
    }

    if (this.isUnifiedMode) {
      this.args.onChange?.(selection);
    } else if (selection.type === 'runbook' && selection.runbook) {
      // Legacy mode - call old callback only if runbook is defined
      this.args.onRunbookChange?.(selection.runbook);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Components::RunbookSelector': typeof RunbookSelector;
  }
}
